import React, { useEffect, useRef, useState } from 'react';
import './PixelStreamingViewer.css';
import { usePixelStreaming } from '../../../hooks/usePixelStreaming';

/**
 * Pixel Streaming Viewer Component
 * Displays Unreal Engine Pixel Streaming in the viewer placeholder
 */
const MAX_AUTO_RETRIES = 3;
const RETRY_DELAY_MS = 5000;

const PixelStreamingViewer = ({ streamUrl, onCommandReady }) => {
  const containerRef = useRef(null);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [errorMessage, setErrorMessage] = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  const {
    isConnected,
    isLoading,
    error,
    videoElementRef,
    initializeStreaming,
    sendCommand,
    sendUIInteraction,
    disconnect
  } = usePixelStreaming(streamUrl);

  // Expose command functions to parent
  useEffect(() => {
    if (onCommandReady && isConnected) {
      onCommandReady({
        sendCommand,
        sendUIInteraction
      });
    }
  }, [isConnected, sendCommand, sendUIInteraction, onCommandReady]);

  // Initialize streaming when component mounts
  useEffect(() => {
    if (streamUrl && containerRef.current) {
      // Append video element to container
      if (videoElementRef.current && !containerRef.current.contains(videoElementRef.current)) {
        containerRef.current.appendChild(videoElementRef.current);
      }
      
      initializeStreaming();
    }

    return () => {
      disconnect();
    };
  }, [streamUrl, initializeStreaming, disconnect, videoElementRef]);

  // Update connection status
  useEffect(() => {
    if (isConnected) {
      setConnectionStatus('connected');
      setErrorMessage(null);
      setRetryCount(0);
    } else if (isLoading) {
      setConnectionStatus('connecting');
    } else if (error) {
      setConnectionStatus('error');
      setErrorMessage(error);
    } else {
      setConnectionStatus('disconnected');
    }
  }, [isConnected, isLoading, error]);

  // Background auto-retry: retry silently, only show error after 3 failures
  useEffect(() => {
    if (connectionStatus !== 'error' || retryCount >= MAX_AUTO_RETRIES) return;
    if (error && error.includes('Stream URL is required')) return;

    const retryTimeout = setTimeout(() => {
      setRetryCount(c => c + 1);
      initializeStreaming();
    }, RETRY_DELAY_MS);

    return () => clearTimeout(retryTimeout);
  }, [connectionStatus, error, retryCount, initializeStreaming]);

  // Debug logging
  useEffect(() => {
    console.log('🔍 PixelStreamingViewer Debug:', {
      streamUrl,
      isConnected,
      isLoading,
      error,
      connectionStatus,
      hasVideoElement: !!videoElementRef.current,
      hasContainer: !!containerRef.current
    });
  }, [streamUrl, isConnected, isLoading, error, connectionStatus]);

  return (
    <div className="pixel-streaming-viewer">
      <div 
        ref={containerRef} 
        className="pixel-streaming-container"
      >
        {/* Video element will be appended here */}
        {(connectionStatus === 'connecting' || (connectionStatus === 'error' && retryCount < MAX_AUTO_RETRIES && !error?.includes('Stream URL is required'))) && (
          <div className="streaming-status connecting">
            <div className="status-spinner"></div>
            <p>Connecting to Unreal Engine...</p>
            <p style={{ fontSize: '0.8rem', marginTop: '0.5rem', opacity: 0.7 }}>
              Stream URL: {streamUrl}
            </p>
          </div>
        )}
        {connectionStatus === 'error' && (retryCount >= MAX_AUTO_RETRIES || error?.includes('Stream URL is required')) && (
          <div className="streaming-status error">
            <i className="fas fa-exclamation-triangle"></i>
            <p>Connection Error</p>
            <p className="pixel-streaming-error-message">
              {retryCount >= MAX_AUTO_RETRIES
                ? 'Unable to connect after several attempts. Please refresh your browser to try again.'
                : errorMessage}
            </p>
          </div>
        )}
        {connectionStatus === 'disconnected' && !isLoading && (
          <div className="streaming-status disconnected">
            <i className="fas fa-plug"></i>
            <p>Not Connected</p>
            <button 
              className="connect-btn"
              onClick={() => initializeStreaming()}
            >
              Connect
            </button>
          </div>
        )}
      </div>
      {isConnected && (
        <div className="streaming-indicator">
          <span className="indicator-dot"></span>
          <span>Streaming</span>
        </div>
      )}
    </div>
  );
};

export default PixelStreamingViewer;

